import { SyntheticEvent, useRef, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { DataTable, DataTableRowClickEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Badge } from "primereact/badge";
import { usePlacementEnvironment } from "../../api/queries/placement-environment/usePlacementEnvironment";
import {
  PlacementEnvironmentDetailChecklist,
  PlacementEnvironmentDetailChecklistStage,
  PlacementEnvironmentDetailChecklistStageTask,
} from "../../api/types/placement-environment";
import { none } from "../common";
import {
  getResponsibleAvatars,
  getStagePercents,
  usePersonOptions,
} from "./shared";
import { SplitButton } from "primereact/splitbutton";
import { formatDate, formatDateTime, orderIndexSort } from "../../common/utils";
import moment from "moment";
import { Institution } from "../../api/types/institution";
import { useContext } from "react";
import { userContext } from "../../context/user";
import { Button } from "primereact/button";
import { OverlayPanel } from "primereact/overlaypanel";
import { useQueryClient } from "react-query";
import { useCreateInstitutionRankingUser } from "../../api/queries/institution-rankings/useCreateInstitutionRankingUser";
import { useInstitutionRankingUser } from "../../api/queries/institution-rankings/useInstitutionRankingUser";
import { InputNumber, InputNumberChangeEvent } from "primereact/inputnumber";
import { queryKeys } from "../../api/query";
import styles from "./styles.module.css";
import { useInstitutionRankings } from "../../api/queries/institution-rankings/useInstitutionRankings";
import { Card } from "primereact/card";

export default function PlacementEnvironmentDetail() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const personOptions = usePersonOptions();
  const { isAdmin, detail } = useContext(userContext);

  const queryClient = useQueryClient();
  const createInstitutionRankingUser = useCreateInstitutionRankingUser();
  const { data: institutionRankingUser } = useInstitutionRankingUser();
  const {
    data: placementEnvironment,
    isLoading: isPlacementEnvironmentLoading,
  } = usePlacementEnvironment(uuid);
  const [institutionRankingUuid, setInstitutionRankingUuid] =
    useState<string>("");
  const { data: institutionRankings, isLoading: isInstitutionRankingLoading } =
    useInstitutionRankings({
      limit: 100,
      offset: 0,
      fCreatedBy: isAdmin() ? undefined : detail?.id,
      fInstitutionId: institutionRankingUuid,
    });

  const myRankingRef = useRef<OverlayPanel | null>(null);
  const myInstitutionRankingRef = useRef<OverlayPanel | null>(null);
  const onInstitutionRankingShow = (event: any, institutionUuid: string) => {
    myInstitutionRankingRef.current?.toggle(event);
    setInstitutionRankingUuid(institutionUuid);
  };

  const onRowClick = (event: DataTableRowClickEvent) => {
    const detail = event.data as PlacementEnvironmentDetailChecklist;

    if (detail.user_checklist_institutions.length > 0) {
      navigate(
        `/journey-trackers/${uuid}/${detail.user_checklist_institutions[0].id}`,
      );
    }
  };

  const getSortedData = () => {
    if (placementEnvironment && institutionRankingUser) {
      return [...placementEnvironment.data.results.checklists].sort(
        (row1, row2) => {
          const r1 = institutionRankingUser.data.results.find(
            (iru) =>
              iru.institution.id === row1.user_checklist_institutions[0].id,
          );
          const r2 = institutionRankingUser.data.results.find(
            (iru) =>
              iru.institution.id === row2.user_checklist_institutions[0].id,
          );

          return (
            (r1?.custom_rank_value || r1?.calculated_rank_value || 0) -
            (r2?.custom_rank_value || r2?.calculated_rank_value || 0)
          );
        },
      );
    }

    return [];
  };

  const tInstitution = (row: PlacementEnvironmentDetailChecklist) => {
    if (row.user_checklist_institutions.length === 0) {
      return <></>;
    }

    return isAdmin() ? (
      <NavLink
        to={`/institutions/${row.user_checklist_institutions[0].id}`}
        className={"text-color"}
      >
        {row.user_checklist_institutions[0].name}
      </NavLink>
    ) : (
      row.user_checklist_institutions[0].name
    );
  };

  const tStupidInstitution = (row: Institution) => {
    return isAdmin() ? (
      <NavLink to={`/institutions/${row.id}`} className={"text-color"}>
        {row.name}
      </NavLink>
    ) : (
      row.name
    );
  };

  const [myRankingInstitution, setMyRankingInstitution] = useState<string>("");
  const [myRanking, setMyRanking] = useState<number | null>(1);

  const onMyRankingChange = (event: InputNumberChangeEvent) => {
    setMyRanking(event.value);
  };

  const onMyRankingShow = (event: any, institutionId: string) => {
    myRankingRef.current?.toggle(event);

    const institutionRanking = institutionRankingUser?.data.results.find(
      (iru) => iru.institution.id === institutionId,
    );

    setMyRanking(institutionRanking ? institutionRanking.custom_rank_value : 1);
    setMyRankingInstitution(institutionId);
  };

  const onMyRankingSave = async () => {
    await createInstitutionRankingUser.mutateAsync({
      institution: myRankingInstitution,
      custom_rank_value: myRanking || 0,
    });
    queryClient.invalidateQueries(queryKeys.institutionRankingUser());
    myRankingRef.current?.hide();
  };

  const onRankingNewClick = () =>
    navigate(
      `/journey-trackers/${uuid}/institution-ranking/${institutionRankingUuid}/new`,
    );

  const onRankingDetailClick = (rankingUuid: string) =>
    navigate(
      `/journey-trackers/${uuid}/institution-ranking/${institutionRankingUuid}/${rankingUuid}`,
    );

  const tMyRanking = (row: PlacementEnvironmentDetailChecklist) => {
    const currentInstitutionRankingUser =
      institutionRankingUser?.data.results.find(
        (iru) => iru.institution.id === row.user_checklist_institutions[0].id,
      );

    const positiveRanking =
      institutionRankings?.data.results.filter(
        (ranking) => ranking.rank_value === 1,
      ) || [];

    const neutralRanking =
      institutionRankings?.data.results.filter(
        (ranking) => ranking.rank_value === 2,
      ) || [];

    const negativeRanking =
      institutionRankings?.data.results.filter(
        (ranking) => ranking.rank_value === 3,
      ) || [];

    return (
      <div className="flex align-items-center">
        <OverlayPanel ref={myRankingRef}>
          <div className="p-inputgroup">
            <InputNumber
              value={myRanking}
              onChange={onMyRankingChange}
              useGrouping={false}
            />
            <Button
              icon="fas fa-save"
              className="p-button-success px-3"
              onClick={onMyRankingSave}
            />
          </div>
        </OverlayPanel>

        <OverlayPanel
          ref={myInstitutionRankingRef}
          style={{ minWidth: 500, backgroundColor: "#f9fafc" }}
        >
          <div
            onClick={(event: SyntheticEvent) => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            <div className="text-right mb-3">
              <Button
                className="p-button-sm p-button-main"
                icon={"pi pi-plus"}
                onClick={onRankingNewClick}
              />
            </div>
            {isInstitutionRankingLoading ? (
              <div className="text-center">
                <i className="pi pi-spin pi-spinner" />
              </div>
            ) : (
              <>
                <div className={styles.rankWrapper}>
                  <div className={styles.rankPositive}>Positive</div>
                  <div className={styles.rankContent}>
                    {positiveRanking.map((ranking) => (
                      <Card
                        key={ranking.id}
                        className={`card-simple ${styles.borderPositive}`}
                        onClick={() => onRankingDetailClick(ranking.id)}
                      >
                        <span
                          dangerouslySetInnerHTML={{ __html: ranking.note }}
                        />
                      </Card>
                    ))}
                  </div>
                </div>
                <div className={styles.rankWrapper}>
                  <div className={styles.rankNeutral}>Neutral</div>
                  <div className={styles.rankContent}>
                    {neutralRanking.map((ranking) => (
                      <Card
                        key={ranking.id}
                        className={`card-simple ${styles.borderNeutral}`}
                        onClick={() => onRankingDetailClick(ranking.id)}
                      >
                        <span
                          dangerouslySetInnerHTML={{ __html: ranking.note }}
                        />
                      </Card>
                    ))}
                  </div>
                </div>
                <div className={styles.rankWrapper}>
                  <div className={styles.rankNegative}>Negative</div>
                  <div className={styles.rankContent}>
                    {negativeRanking.map((ranking) => (
                      <Card
                        key={ranking.id}
                        className={`card-simple ${styles.borderNegative}`}
                        onClick={() => onRankingDetailClick(ranking.id)}
                      >
                        <span
                          dangerouslySetInnerHTML={{ __html: ranking.note }}
                        />
                      </Card>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </OverlayPanel>

        <strong className="mr-3">
          #{currentInstitutionRankingUser?.custom_rank_value || 1}
        </strong>

        <Button
          icon="fas fa-pen"
          className="p-button-text p-button-secondary"
          onClick={(event) =>
            onMyRankingShow(event, row.user_checklist_institutions[0].id)
          }
        />

        <Button
          icon="fas fa-rectangle-list"
          className="p-button-text p-button-secondary"
          onClick={(event) =>
            onInstitutionRankingShow(
              event,
              row.user_checklist_institutions[0].id,
            )
          }
        />
      </div>
    );
  };

  const [stagePercent, setStagePercent] = useState<string>("");
  const currentStatusRef = useRef<OverlayPanel | null>(null);
  const onCurrentStatusShow = (
    event: any,
    stage: PlacementEnvironmentDetailChecklistStage,
  ) => {
    setStagePercent(getStagePercents(stage));
    currentStatusRef.current?.toggle(event);
  };

  const tCurrentStatus = (row: PlacementEnvironmentDetailChecklist) => {
    const firstStageWithUnfinishedTask = getFirstStageWithUnfinishedTasks(row);

    if (!firstStageWithUnfinishedTask) {
      return "Finished";
    }

    let likelihood = 0;

    if (
      row.user_checklist_institutions.length > 0 &&
      row.user_checklist_institutions[0].institution_likelihood !== null
    ) {
      likelihood =
        row.user_checklist_institutions[0].institution_likelihood.likelihood;
    }

    return (
      <div className="flex align-items-center">
        <div className="mr-3">
          <Badge
            value={firstStageWithUnfinishedTask.name}
            severity={"warning"}
            className="p-badge-pe"
          />
        </div>

        <div className="flex align-items-center">
          <span className="block text-red-500 white-space-nowrap">
            {likelihood.toFixed(0)}% complete
          </span>

          <Button
            icon="fas fa-info-circle"
            className="p-button-text p-button-secondary"
            onClick={(event) =>
              onCurrentStatusShow(event, firstStageWithUnfinishedTask)
            }
          />

          <OverlayPanel ref={currentStatusRef}>
            Progress within the overall checklist.
            <br />
            Progress within the stage is {stagePercent}%.
          </OverlayPanel>
        </div>
      </div>
    );
  };

  const getLastActivity = (
    row: PlacementEnvironmentDetailChecklist,
  ): PlacementEnvironmentDetailChecklistStageTask | null => {
    const completedTasks: PlacementEnvironmentDetailChecklistStageTask[] = [];

    row.checklist_stages.forEach((stage) =>
      stage.checklist_tasks_user.forEach((task) => {
        if (task.completed) {
          completedTasks.push(Object.assign({}, task));
        }
      }),
    );

    let lastCompletedTask: PlacementEnvironmentDetailChecklistStageTask | null =
      null;
    let mLastCompletedTaskDate = moment("1990-01-01");

    completedTasks.forEach((task) => {
      const mCompletedAt = moment(task.completed_at);

      if (
        mCompletedAt.isValid() &&
        mCompletedAt.isAfter(mLastCompletedTaskDate)
      ) {
        lastCompletedTask = Object.assign({}, task);
        mLastCompletedTaskDate = mCompletedAt;
      }
    });

    return lastCompletedTask;
  };

  const tLastActivity = (row: PlacementEnvironmentDetailChecklist) => {
    const lastActivity = getLastActivity(row);

    if (!lastActivity) {
      return none;
    }

    return (
      <>
        <div>{lastActivity.name}</div>
        <small>{formatDateTime(lastActivity.completed_at || "")}</small>
      </>
    );
  };

  const tLastActivityPersonResponsible = (
    row: PlacementEnvironmentDetailChecklist,
  ) => {
    const lastActivity = getLastActivity(row);

    if (!lastActivity) {
      return none;
    }

    return getResponsibleAvatars(
      personOptions,
      lastActivity.interaction_people,
    );
  };

  const getFirstStageWithUnfinishedTasks = (
    row: PlacementEnvironmentDetailChecklist,
  ): PlacementEnvironmentDetailChecklistStage | null => {
    const firstStageWithUnfinishedTask = row.checklist_stages
      .sort(orderIndexSort)
      .find((stage) =>
        stage.checklist_tasks_user.some((task) => task.completed === false),
      );

    if (!firstStageWithUnfinishedTask) {
      return null;
    }

    return firstStageWithUnfinishedTask;
  };

  const getNextActivity = (
    row: PlacementEnvironmentDetailChecklist,
  ): PlacementEnvironmentDetailChecklistStageTask | null => {
    const firstStageWithUnfinishedTask = getFirstStageWithUnfinishedTasks(row);

    if (!firstStageWithUnfinishedTask) {
      return null;
    }

    const firstUnfinishedTask =
      firstStageWithUnfinishedTask.checklist_tasks_user
        .sort(orderIndexSort)
        .find((tasks) => !tasks.completed);

    if (!firstUnfinishedTask) {
      return null;
    }

    return firstUnfinishedTask;
  };

  const [nextActivityDesc, setNextActivityDesc] = useState<string>("");
  const nextActivityRef = useRef<OverlayPanel | null>(null);
  const onNextActivityDescShow = (event: any, nextActivityDesc: string) => {
    setNextActivityDesc(nextActivityDesc);
    nextActivityRef.current?.toggle(event);
  };

  const tNextActivity = (row: PlacementEnvironmentDetailChecklist) => {
    const nextActivity = getNextActivity(row);

    if (!nextActivity) {
      return none;
    }

    return (
      <div className="flex align-items-center">
        <div>{nextActivity.name}</div>

        <Button
          icon="fas fa-info-circle"
          className="p-button-text p-button-secondary"
          onClick={(event) =>
            onNextActivityDescShow(event, nextActivity.description)
          }
        />

        <OverlayPanel ref={nextActivityRef}>{nextActivityDesc}</OverlayPanel>
      </div>
    );
  };

  const tNextActivityDueDate = (row: PlacementEnvironmentDetailChecklist) => {
    const nextActivity = getNextActivity(row);

    if (!nextActivity || !nextActivity.due_date) {
      return none;
    }

    const mNow = moment();
    const mDueDate = moment(nextActivity.due_date);
    const days = mDueDate.diff(mNow, "days");

    return (
      <>
        <div>{formatDate(nextActivity.due_date)}</div>
        {days < 0 ? (
          <small>(before {days} days)</small>
        ) : (
          <small>({days} more days)</small>
        )}
      </>
    );
  };

  const tNextActivityPersonResponsible = (
    row: PlacementEnvironmentDetailChecklist,
  ) => {
    const nextActivity = getNextActivity(row);

    if (!nextActivity) {
      return none;
    }

    return getResponsibleAvatars(
      personOptions,
      nextActivity.interaction_people || [],
    );
  };

  return (
    <div className={"col-12"}>
      <div className="flex py-3">
        <h1 className="m-0">My Journey Tracker</h1>

        <SplitButton
          className="p-splitbutton-main ml-auto"
          label="Add new"
          size="small"
          model={[
            {
              label: "Institution",
              command: () => navigate(`/journey-trackers/${uuid}/institution`),
            },
            {
              label: "Representative",
              command: () => navigate("/representatives/new"),
            },
          ]}
        />
      </div>

      {placementEnvironment &&
      placementEnvironment.data.results.checklists.length === 0 ? (
        <DataTable
          emptyMessage={"No records"}
          paginator={false}
          lazy={true}
          className="p-datatable-sm p-datatable-pointer"
          stripedRows={true}
          value={placementEnvironment?.data.results.institutions || []}
        >
          <Column header={"Institution"} body={tStupidInstitution} />

          <Column header={"My ranking"} body={() => none} />

          <Column header={"Current status"} body={() => none} />

          <Column header={"Last activity"} body={() => none} />

          <Column header={"Person responsible"} body={() => none} />

          <Column header={"Next activity"} body={() => none} />

          <Column header={"Due date"} body={() => none} />

          <Column header={"Person responsible"} body={() => none} />
        </DataTable>
      ) : (
        <DataTable
          emptyMessage={"No records"}
          paginator={false}
          lazy={true}
          className="p-datatable-sm p-datatable-pointer"
          onRowClick={onRowClick}
          stripedRows={true}
          value={getSortedData()}
          loading={isPlacementEnvironmentLoading}
        >
          <Column header={"Institution"} body={tInstitution} />

          <Column header={"My ranking"} body={tMyRanking} />

          <Column header={"Current status"} body={tCurrentStatus} />

          <Column header={"Last activity"} body={tLastActivity} />

          <Column
            header={"Person responsible"}
            body={tLastActivityPersonResponsible}
          />

          <Column header={"Next activity"} body={tNextActivity} />

          <Column header={"Due date"} body={tNextActivityDueDate} />

          <Column
            header={"Person responsible"}
            body={tNextActivityPersonResponsible}
          />

          {/* <Column header={"Actions"} body={tActions} /> */}
        </DataTable>
      )}
    </div>
  );
}
